// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useTransferInstructions } from '@hooks/queries/registry/transferInstruction/useTransferInstructions';
import { useGetAllInstrumentConfigurations } from '@hooks/queries/useGetAllInstrumentConfigurations';
import React, { useState } from 'react';
import { GridRowParams } from '@mui/x-data-grid';
import { TransferInstruction } from '@daml.js/splice-api-token-transfer-instruction/lib/Splice/Api/Token/TransferInstructionV1';
import { Batch } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Types';
import { ChoiceDialog } from '../../../components/ChoiceDialog';
import Loading from '../../../components/Loading';
import { DataTable } from '../../../components/Table/DataTable';
import useRegistrarServices from '../../../hooks/queries/registry/onboarding/useRegistrarServices';
import useAcceptedTransfers from '../../../hooks/queries/registry/transfer/useAcceptedTransfers';
import useExecutedTransfers from '../../../hooks/queries/registry/transfer/useExecutedTransfers';
import useFailedTransfers from '../../../hooks/queries/registry/transfer/useFailedTransfers';
import useRejectedTransfers from '../../../hooks/queries/registry/transfer/useRejectedTransfers';
import useHoldings from '../../../hooks/queries/registry/useHoldings';
import { filterInstConfigsByInstrument } from '../../../utils/filterInstConfigsByInstrument';
import { PageWrapper } from '../../PageWrapper';
import { OfferTransferDialog } from './OfferTransferDialog';
import { TransferDetails } from './TransferDetails';
import { createHeaders } from './createHeaders';
import { Row, TransferExtended } from './types';
import { useCreateTransferActions } from './useCreateTransferActions';

export const Transfers = () => {
  const registrarServices = useRegistrarServices();
  const holdings = useHoldings();
  const instConfigs = useGetAllInstrumentConfigurations();
  const acceptedTransfers = useAcceptedTransfers();
  const rejectedTransfers = useRejectedTransfers();
  const executedTransfers = useExecutedTransfers();
  const failedTransfers = useFailedTransfers();
  const transferInstructions = useTransferInstructions();
  const [dialogName, setDialogName] = React.useState('');
  const onCloseDialog = () => {
    setDialogName('');
  };
  const [selectedRow, setSelectedRow] = useState<Row | undefined>(undefined);
  const onRowClick = (r: GridRowParams<Row>) => {
    setSelectedRow(r.row);
  };
  const onClose = () => {
    setSelectedRow(undefined);
  };
  const actions = useCreateTransferActions(onClose, selectedRow);

  const isLoading =
    registrarServices.isLoading ||
    holdings.isLoading ||
    acceptedTransfers.isLoading ||
    rejectedTransfers.isLoading ||
    executedTransfers.isLoading ||
    failedTransfers.isLoading ||
    !holdings.data ||
    !acceptedTransfers.data ||
    !rejectedTransfers.data ||
    !executedTransfers.data ||
    !failedTransfers.data ||
    transferInstructions.isLoading ||
    !transferInstructions.data;
  if (isLoading) return <Loading />;
  const rows = transferInstructions.data
    .map((r) => {
      const { transfer } = r.view(TransferInstruction);
      const config = filterInstConfigsByInstrument(
        transfer.instrumentId.id,
        transfer.instrumentId.admin,
        instConfigs.data,
      );
      const operator = config?.payload.operator || '';
      const provider = config?.payload.provider || '';
      const payload: TransferExtended = {
        sender: transfer.sender,
        receiver: transfer.receiver,
        instrumentIdentifier: {
          source: transfer.instrumentId.admin,
          id: transfer.instrumentId.id,
          scheme: 'RegistrarInternalScheme',
        },
        executeBefore: transfer.executeBefore,
        batch: {} as Batch,
        amount: transfer.amount,
        registrar: transfer.instrumentId.admin,
        reference: transfer.meta.values['splice.lfdecentralizedtrust.org/reason'],
        provider,
        operator,
      };
      return {
        contractId: r.contractId as string,
        state: `Offered2step`,
        payload,
      } as Row;
    })
    .concat(
      acceptedTransfers.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Accepted',
        payload: r.payload.transfer,
      })),
    )
    .concat(
      rejectedTransfers.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Rejected',
        payload: r.payload.transfer,
        reason: r.payload.reason,
      })),
    )
    .concat(
      executedTransfers.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Executed',
        payload: r.payload.transfer,
      })),
    )
    .concat(
      failedTransfers.data.map((r) => ({
        contractId: r.contractId as string,
        state: `Failed`,
        payload: r.payload.transfer,
        reason: r.payload.reason,
      })),
    );
  const buttons = [
    {
      name: 'offer transfer',
      onClick: () => setDialogName('transfer'),
    },
  ];
  return (
    <PageWrapper buttons={buttons} pageName="Transfers">
      <ChoiceDialog
        open={!!selectedRow}
        onClose={onClose}
        actions={actions}
        title="Transfer Details"
      >
        {selectedRow && <TransferDetails row={selectedRow} />}
      </ChoiceDialog>
      {dialogName === 'transfer' && <OfferTransferDialog open onClose={onCloseDialog} />}
      <DataTable
        columns={createHeaders()}
        rows={rows}
        onRowClick={onRowClick}
        rowKey={(a) => a.contractId}
      />
    </PageWrapper>
  );
};
